import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const HTMLUrls = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Links & Navigation</div>
                <h1>URL Structure & Paths (Absolute vs Relative)</h1>
                <p className="hero-lead">
                    Master URL anatomy and path types. Learn when to use absolute vs. relative paths,
                    understand URL components, and avoid common linking mistakes.
                </p>
            </div>

            <div className="content-section">
                <h2>What is a URL?</h2>
                <p>
                    A <strong>URL (Uniform Resource Locator)</strong> is the address used to locate resources on the web.
                    Understanding URL structure is essential for creating correct links.
                </p>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Fun Fact:</strong> URLs are a type of URI (Uniform Resource Identifier).
                        All URLs are URIs, but not all URIs are URLs.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Anatomy of a URL</h2>
                <p>
                    Let's break down a complete URL into its components:
                </p>

                <div className="url-diagram">
                    <CodeBlock language="bash" code={`https://www.example.com:443/blog/article?id=123&sort=date#section-2
                                                        
Scheme  Subdomain Domain Port Path  File    Query String   Fragment

Full breakdown:
 Protocol/Scheme: https://
 Host/Domain: www.example.com
   Subdomain: www
   Domain: example
   TLD: .com
 Port: :443 (optional, default for https)
 Path: /blog/article
 Query String: ?id=123&sort=date
 Fragment: #section-2`} />
                </div>

                <div className="component-details">
                    <h3>URL Components Explained:</h3>

                    <div className="component-card">
                        <h4>1. Scheme/Protocol</h4>
                        <p>Defines how to access the resource</p>
                        <ul>
                            <li><code>http://</code> - Unencrypted HyperText Transfer Protocol</li>
                            <li><code>https://</code> - Secure HTTP (encrypted with SSL/TLS)</li>
                            <li><code>ftp://</code> - File Transfer Protocol</li>
                            <li><code>mailto:</code> - Email address</li>
                            <li><code>tel:</code> - Telephone number</li>
                            <li><code>file://</code> - Local file system</li>
                        </ul>
                    </div>

                    <div className="component-card">
                        <h4>2. Domain/Host</h4>
                        <p>The server address where the resource is located</p>
                        <CodeBlock language="bash" code={`www.example.com
 www (subdomain)
 example (domain name)
 .com (top-level domain)`} />
                    </div>

                    <div className="component-card">
                        <h4>3. Port</h4>
                        <p>Optional - network port on the server</p>
                        <ul>
                            <li>Default ports (usually omitted):
                                <ul>
                                    <li>HTTP: port 80</li>
                                    <li>HTTPS: port 443</li>
                                </ul>
                            </li>
                            <li>Custom ports must be specified: <code>:8080</code>, <code>:3000</code></li>
                        </ul>
                    </div>

                    <div className="component-card">
                        <h4>4. Path</h4>
                        <p>Location of the resource on the server</p>
                        <CodeBlock language="bash" code={`/blog/articles/html-guide
 /blog (directory)
 /articles (subdirectory)
 /html-guide (file or route)`} />
                    </div>

                    <div className="component-card">
                        <h4>5. Query String</h4>
                        <p>Parameters passed to the resource</p>
                        <CodeBlock language="bash" code={`?id=123&category=html&sort=date
 ? (starts query string)
 id=123 (parameter 1)
 & (separator)
 category=html (parameter 2)
 & (separator)
 sort=date (parameter 3)`} />
                    </div>

                    <div className="component-card">
                        <h4>6. Fragment/Hash</h4>
                        <p>Specific section within the resource</p>
                        <CodeBlock language="bash" code={`#section-2
 Jumps to element with id="section-2"`} />
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Absolute URLs</h2>
                <p>
                    <strong>Absolute URLs</strong> contain the complete address, including protocol and domain.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Absolute URL Examples</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Complete URL with all parts --&gt;
<a href="https://www.example.com/blog/article.html">Article</a>

<!-- External site link --&gt;
<a href="https://www.wikipedia.org">Wikipedia</a>

<!-- Secure vs non-secure --&gt;
<a href="https://secure-site.com">HTTPS Site</a>
<a href="http://old-site.com">HTTP Site</a>

<!-- With port number --&gt;
<a href="http://localhost:3000/app">Local Dev Server</a>`} />
                </div>

                <h3>When to Use Absolute URLs:</h3>
                <div className="use-cases">
                    <ul>
                        <li>Links to external websites</li>
                        <li>Email links (<code>href</code> in HTML emails)</li>
                        <li>Canonical URLs (SEO)</li>
                        <li>Open Graph tags (social media)</li>
                        <li>RSS/XML feeds</li>
                        <li>API endpoints</li>
                    </ul>
                </div>

                <h3>Advantages:</h3>
                <ul>
                    <li>Work from anywhere (portable)</li>
                    <li>Clear and unambiguous</li>
                    <li>Required for external resources</li>
                </ul>

                <h3>Disadvantages:</h3>
                <ul>
                    <li>Longer and more verbose</li>
                    <li>Harder to move site to new domain</li>
                    <li>Can't easily test on different environments</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Relative URLs</h2>
                <p>
                    <strong>Relative URLs</strong> specify a path relative to the current page's location.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Relative URL Examples</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Relative to current directory --&gt;
<a href="about.html">About Page</a>

<!-- Subdirectory --&gt;
<a href="blog/article.html">Blog Article</a>

<!-- Parent directory --&gt;
<a href="../index.html">Parent Level</a>

<!-- Root-relative (starts with /) --&gt;
<a href="/contact">Contact Page</a>

<!-- Same page anchor --&gt;
<a href="#section-2">Jump to Section 2</a>

<!-- Query string only --&gt;
<a href="?page=2">Page 2</a>`} />
                </div>

                <h3>Types of Relative URLs:</h3>

                <div className="relative-types">
                    <div className="type-card">
                        <h4>1. Document-Relative</h4>
                        <p>Relative to the current page's location</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`Current page: /blog/articles/post.html

<a href="other-post.html">
 /blog/articles/other-post.html

<a href="images/photo.jpg">
 /blog/articles/images/photo.jpg`} />
                        </div>
                    </div>

                    <div className="type-card">
                        <h4>2. Root-Relative</h4>
                        <p>Relative to the site root (starts with <code>/</code>)</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`Current page: /blog/articles/post.html

<a href="/about">
 /about (from root)

<a href="/images/logo.png">
 /images/logo.png (from root)`} />
                        </div>
                    </div>

                    <div className="type-card">
                        <h4>3. Protocol-Relative</h4>
                        <p>Uses current protocol (http or https)</p>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Inherits protocol from current page --&gt;
<a href="//example.com/page">Link</a>

If on https://mysite.com:
 https://example.com/page

If on http://mysite.com:
 http://example.com/page`} />
                        </div>
                    </div>
                </div>

                <h3>When to Use Relative URLs:</h3>
                <ul>
                    <li>Internal site navigation</li>
                    <li>Images, CSS, and JavaScript files on same domain</li>
                    <li>Development (works on localhost and production)</li>
                    <li>Easier site migration</li>
                </ul>

                <h3>Advantages:</h3>
                <ul>
                    <li>Shorter and cleaner</li>
                    <li>Portable between environments (dev, staging, prod)</li>
                    <li>Works with or without www prefix</li>
                    <li>Automatically uses correct protocol</li>
                </ul>

                <h3>Disadvantages:</h3>
                <ul>
                    <li>Can be confusing with multiple directory levels</li>
                    <li>Break if page moves to different location</li>
                    <li>Don't work in emails or external contexts</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Navigating Directory Structures</h2>
                <p>
                    Understanding how to navigate directories with relative paths:
                </p>

                <div className="directory-structure">
                    <h3>Example Site Structure:</h3>
                    <pre><code>{`website-root/
 index.html
 about.html
 blog/
    index.html
    post-1.html
    post-2.html
 images/
    logo.png
    banner.jpg
 assets/
     css/
        style.css
     js/
         script.js`}</code></pre>
                </div>

                <div className="navigation-examples">
                    <h3>Navigation from Different Locations:</h3>

                    <div className="example-card">
                        <h4>From: <code>/index.html</code></h4>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Same directory --&gt;
<a href="about.html">About</a>
 /about.html

<!-- Subdirectory --&gt;
<a href="blog/index.html">Blog</a>
 /blog/index.html

<!-- Image in subdirectory --&gt;
<img src="images/logo.png">
 /images/logo.png

<!-- Root-relative --&gt;
<a href="/about.html">About</a>
 /about.html`} />
                        </div>
                    </div>

                    <div className="example-card">
                        <h4>From: <code>/blog/post-1.html</code></h4>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Same directory --&gt;
<a href="post-2.html">Post 2</a>
 /blog/post-2.html

<!-- Parent directory --&gt;
<a href="../index.html">Home</a>
 /index.html

<!-- Parent directory file --&gt;
<a href="../about.html">About</a>
 /about.html

<!-- Different subdirectory (go up, then down) --&gt;
<img src="../images/logo.png">
 /images/logo.png

<!-- Root-relative (easier!) --&gt;
<a href="/index.html">Home</a>
 /index.html

<img src="/images/logo.png">
 /images/logo.png`} />
                        </div>
                    </div>
                </div>

                <div className="path-symbols">
                    <h3>Special Path Symbols:</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Symbol</th>
                                <th>Meaning</th>
                                <th>Example</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>/</code></td>
                                <td>Root directory (at start)</td>
                                <td><code>/index.html</code></td>
                            </tr>
                            <tr>
                                <td><code>./</code></td>
                                <td>Current directory</td>
                                <td><code>./about.html</code></td>
                            </tr>
                            <tr>
                                <td><code>../</code></td>
                                <td>Parent directory (up one level)</td>
                                <td><code>../index.html</code></td>
                            </tr>
                            <tr>
                                <td><code>../../</code></td>
                                <td>Up two levels</td>
                                <td><code>../../index.html</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Query Strings & Parameters</h2>
                <p>
                    Query strings pass data to pages through the URL:
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Query String Syntax</span>
                    </div>
                    <CodeBlock language="html" code={`<!-- Single parameter --&gt;
<a href="search.html?q=html">Search for HTML</a>
 /search.html?q=html

<!-- Multiple parameters --&gt;
<a href="shop.html?category=books&sort=price&page=2">Books</a>
 /shop.html?category=books&sort=price&page=2

<!-- With fragment --&gt;
<a href="article.html?id=123#section-2">Article Section</a>
 /article.html?id=123#section-2

<!-- URL encoding special characters --&gt;
<a href="search.html?q=html%20%26%20css">Search "html & css"</a>
 /search.html?q=html%20%26%20css`} />
                </div>

                <h3>Common URL Encoding:</h3>
                <div className="encoding-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Character</th>
                                <th>Encoded</th>
                                <th>Usage</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Space</td>
                                <td><code>%20</code> or <code>+</code></td>
                                <td><code>hello%20world</code></td>
                            </tr>
                            <tr>
                                <td>&amp;</td>
                                <td><code>%26</code></td>
                                <td><code>tom%26jerry</code></td>
                            </tr>
                            <tr>
                                <td>=</td>
                                <td><code>%3D</code></td>
                                <td><code>2%2B2%3D4</code></td>
                            </tr>
                            <tr>
                                <td>?</td>
                                <td><code>%3F</code></td>
                                <td><code>what%3F</code></td>
                            </tr>
                            <tr>
                                <td>/</td>
                                <td><code>%2F</code></td>
                                <td><code>http%3A%2F%2F</code></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>Common URL Mistakes</h2>

                <div className="mistakes-grid">
                    <div className="mistake-card">
                        <h3> Mistake #1: Missing Protocol</h3>
                        <div className="code-example error">
                            <CodeBlock language="html" code={`<a href="www.example.com">Link</a>
<!-- Treated as relative path: /www.example.com --&gt;`} />
                        </div>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Correct --&gt;
<a href="https://www.example.com">Link</a>`} />
                        </div>
                    </div>

                    <div className="mistake-card">
                        <h3> Mistake #2: Wrong Relative Path</h3>
                        <div className="code-example error">
                            <CodeBlock language="html" code={`<!-- From /blog/post.html, trying to link to /about.html --&gt;
<a href="about.html">About</a>
<!-- Goes to /blog/about.html (wrong!) --&gt;`} />
                        </div>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Correct options --&gt;
<a href="../about.html">About</a>  <!-- Up one level --&gt;
<a href="/about.html">About</a>     <!-- Root-relative --&gt;`} />
                        </div>
                    </div>

                    <div className="mistake-card">
                        <h3> Mistake #3: Spaces in URLs</h3>
                        <div className="code-example error">
                            <CodeBlock language="html" code={`<a href="my page.html">Link</a>
<!-- Breaks! Spaces must be encoded --&gt;`} />
                        </div>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Correct --&gt;
<a href="my-page.html">Link</a>        <!-- Use hyphens --&gt;
<a href="my%20page.html">Link</a>      <!-- Or encode --&gt;`} />
                        </div>
                    </div>

                    <div className="mistake-card">
                        <h3> Mistake #4: Mixed Protocols</h3>
                        <div className="code-example error">
                            <CodeBlock language="html" code={`<!-- On HTTPS site, linking to HTTP --&gt;
<img src="http://example.com/image.jpg">
<!-- Mixed content warning! --&gt;`} />
                        </div>
                        <div className="code-example">
                            <CodeBlock language="html" code={`<!-- Correct --&gt;
<img src="https://example.com/image.jpg">  <!-- HTTPS --&gt;
<img src="//example.com/image.jpg">        <!-- Protocol-relative --&gt;`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use root-relative paths for internal navigation</li>
                            <li>Use absolute URLs for external links</li>
                            <li>Encode special characters in URLs</li>
                            <li>Use lowercase for consistency</li>
                            <li>Use hyphens instead of spaces</li>
                            <li>Test all links after moving files</li>
                            <li>Use trailing slashes consistently</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Forget protocol on external links</li>
                            <li>Use spaces in URLs</li>
                            <li>Mix relative path levels unnecessarily</li>
                            <li>Use absolute URLs for internal links</li>
                            <li>Include sensitive data in query strings</li>
                            <li>Create overly long URLs</li>
                            <li>Use special characters without encoding</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Quick Reference</h2>
                <div className="reference-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Path Type</th>
                                <th>Example</th>
                                <th>Starts With</th>
                                <th>Use Case</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Absolute</td>
                                <td><code>https://example.com/page</code></td>
                                <td><code>http://</code> or <code>https://</code></td>
                                <td>External links</td>
                            </tr>
                            <tr>
                                <td>Root-relative</td>
                                <td><code>/about</code></td>
                                <td><code>/</code></td>
                                <td>Internal navigation</td>
                            </tr>
                            <tr>
                                <td>Document-relative</td>
                                <td><code>page.html</code></td>
                                <td>Filename</td>
                                <td>Same directory</td>
                            </tr>
                            <tr>
                                <td>Parent</td>
                                <td><code>../page.html</code></td>
                                <td><code>../</code></td>
                                <td>Up one level</td>
                            </tr>
                            <tr>
                                <td>Current</td>
                                <td><code>./page.html</code></td>
                                <td><code>./</code></td>
                                <td>Current directory</td>
                            </tr>
                            <tr>
                                <td>Protocol-relative</td>
                                <td><code>//example.com/page</code></td>
                                <td><code>//</code></td>
                                <td>Inherit protocol</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
};

export default HTMLUrls;



